import React from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, Switch, Card, CardContent } from '@mui/material';
import SoftTypography from 'components/SoftTypography';
import SoftSelect from 'components/SoftSelect';
import typography from '../../../../assets/theme/base/typography';
import { useTranslation } from 'react-i18next';
import SoftDatePicker from 'components/SoftDatePicker';
import { CONFIG_APP } from 'shared/const/app.config';
import dayjs from 'dayjs';

export const paymentMethodValues = [
  { label: 'Credit Card', value: 'CCR' },
  { label: 'On Account', value: 'OnAccount' },
];

const AgencyDetailsForm = ({
  formData,
  formErrors,
  isEditMode,
  isNewAgency,
  setFormData,
}) => {
  const { t } = useTranslation();
  const countries = ['AU', 'NZ'];
  const brands = {
    AU: ['FC', 'FCI', 'IH', 'LK', 'TA', 'TP', 'LT'],
    NZ: ['ENV', 'GTN', 'TM', 'TP', 'TA', 'FTB'],
  };
  const partners = ['FLT', 'EA'];

  const boolValues = [
    { label: t('COMMON.yes'), value: true },
    { label: t('COMMON.no'), value: false },
  ];

  const getMerchantOfRecordValue = () => {
    if (formData.merchantOfRecord && !isEditMode)
      return formData.merchantOfRecord;
    if (!formData?.isTrusted && !formData?.paymentMethod) return '';
    if (formData.isTrusted) return 'FLT';
    if (formData.paymentMethod.toLowerCase() === 'onaccount') return 'IND';
    return 'EA';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(name, value);
  };

  const handleDateChange = (event) => {
    setFormData(
      'agreementSignedDate',
      event[0] ? dayjs(event[0]).format() : '',
    );
  };

  const renderTextField = (name, disabled = false) => (
    <FormField
      label={t(`AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.${name}`)}
      isEditMode={isEditMode}
      error={formErrors[name]}
      helperText={
        formErrors[name]
          ? t(`AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.${name}Required`)
          : ''
      }
      readOnlyValue={formData[name]}
    >
      <TextField
        fullWidth
        sx={{
          '& .MuiInputBase-input': {
            width: '100% !important',
          },
        }}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        error={formErrors[name]}
        helperText={
          formErrors[name]
            ? t(`AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.${name}Required`)
            : ''
        }
        disabled={disabled}
      />
    </FormField>
  );

  return (
    <Card>
      <CardContent sx={{ ml: 3, mr: 3 }}>
        <Grid container mt={3} mb={3} spacing={2}>
          <Grid item xs={6}>
            {renderTextField('identifier')}
          </Grid>
          <Grid item xs={6}>
            {renderTextField('email')}
          </Grid>
          <Grid item xs={6}>
            {renderTextField('shopName')}
          </Grid>
          <Grid item xs={6}>
            {renderTextField('mailingAddress')}
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.partner')}
              isEditMode={isEditMode}
              readOnlyValue={formData.partner}
            >
              <SoftSelect
                name="partner"
                value={
                  formData.partner
                    ? {
                        value: formData.partner,
                        label: formData.partner,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  setFormData('partner', selectedOption.value);
                }}
                options={partners.map((partner) => ({
                  value: partner,
                  label: partner,
                }))}
                error={formErrors.partner}
                helperText={
                  formErrors.partner
                    ? t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.partnerRequired')
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            {renderTextField('abn')}
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.country')}
              isEditMode={isEditMode}
              readOnlyValue={formData.country}
            >
              <SoftSelect
                name="country"
                value={
                  formData.country
                    ? {
                        value: formData.country,
                        label: formData.country,
                      }
                    : undefined
                }
                onChange={(selectedOption) => {
                  setFormData('country', selectedOption.value);
                  setFormData('brands', []);
                }}
                options={countries.map((country) => ({
                  value: country,
                  label: country,
                }))}
                error={formErrors.country}
                helperText={
                  formErrors.country
                    ? t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.countryRequired')
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.brands')}
              isEditMode={isEditMode}
              readOnlyValue={formData.brands.join(', ')}
            >
              <SoftSelect
                name="brands"
                value={formData.brands.map((brand) => ({
                  value: brand,
                  label: brand,
                }))}
                onChange={(selectedOption) => {
                  setFormData(
                    'brands',
                    selectedOption ? [selectedOption.value] : [],
                  );
                }}
                options={
                  brands[formData.country]
                    ? brands[formData.country].map((brand) => ({
                        value: brand,
                        label: brand,
                      }))
                    : []
                }
                error={formErrors.brands}
                helperText={
                  formErrors.brands
                    ? t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.brandsRequired')
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            {renderTextField('bankAccountInfo')}
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.isActive')}
              isEditMode={isEditMode && !isNewAgency}
              readOnlyValue={
                isNewAgency
                  ? t('COMMON.yes')
                  : formData.isActive
                  ? t('COMMON.yes')
                  : t('COMMON.no')
              }
            >
              <SoftSelect
                name="isActive"
                value={boolValues.find(
                  (value) => value.value === formData.isActive,
                )}
                onChange={(selectedOption) => {
                  setFormData('isActive', selectedOption.value);
                }}
                options={boolValues}
                error={formErrors.isActive}
                helperText={
                  formErrors.isActive
                    ? t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.isActiveRequired')
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            {renderTextField('phoneNumber')}
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t(
                'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.isAgreementSigned',
              )}
              isEditMode={isEditMode}
              readOnlyValue={
                formData.isAgreementSigned ? t('COMMON.yes') : t('COMMON.no')
              }
            >
              <SoftSelect
                name="isAgreementSigned"
                value={boolValues.find(
                  (value) => value.value === formData.isAgreementSigned,
                )}
                onChange={(selectedOption) => {
                  setFormData('isAgreementSigned', selectedOption.value);
                }}
                options={boolValues}
                error={formErrors.isAgreementSigned}
                helperText={
                  formErrors.isAgreementSigned
                    ? t(
                        'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.isAgreementSignedRequired',
                      )
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t(
                'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.agreementSignedDate',
              )}
              isEditMode={isEditMode}
              error={formErrors.agreementSignedDate}
              helperText={
                formErrors.agreementSignedDate
                  ? t(
                      'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.agreementSignedDateRequired',
                    )
                  : ''
              }
              readOnlyValue={
                formData.agreementSignedDate
                  ? dayjs(formData.agreementSignedDate).format(
                      CONFIG_APP.DATE_FORMAT_DATEPICKER,
                    )
                  : ''
              }
            >
              <SoftDatePicker
                input={{
                  required: true,
                  placeholder: t(
                    'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.agreementSignedDate',
                  ),
                  error: formErrors.agreementSignedDate,
                }}
                options={{
                  dateFormat: CONFIG_APP.DATE_FORMAT_DATEPICKER,
                  parseDate: function (dateStr, format) {
                    return dayjs(dateStr, { format }).toDate();
                  },
                  formatDate: function (date, format) {
                    return dayjs(date).format(format);
                  },
                }}
                value={formData.agreementSignedDate}
                onChange={handleDateChange}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.isTrusted')}
              isEditMode={isEditMode}
              readOnlyValue={
                formData.isTrusted ? t('COMMON.yes') : t('COMMON.no')
              }
            >
              <SoftSelect
                name="isTrusted"
                value={boolValues.find(
                  (value) => value.value === formData.isTrusted,
                )}
                onChange={(selectedOption) => {
                  setFormData('isTrusted', selectedOption.value);
                  setFormData(
                    'paymentMethod',
                    selectedOption.value ? 'OnAccount' : '',
                  );
                }}
                options={boolValues}
                error={formErrors.isTrusted}
                helperText={
                  formErrors.isTrusted
                    ? t(
                        'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.isTrustedRequired',
                      )
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.merchantOfRecord')}
              isEditMode={false}
              readOnlyValue={getMerchantOfRecordValue()}
            />
          </Grid>
          <Grid item xs={6}>
            <FormField
              label={t('AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.paymentMethod')}
              isEditMode={isEditMode}
              readOnlyValue={
                paymentMethodValues.find(
                  (paymentMethod) =>
                    paymentMethod.value === formData.paymentMethod,
                )?.label || formData.paymentMethod
              }
            >
              <SoftSelect
                name="paymentMethod"
                value={
                  formData.paymentMethod
                    ? {
                        value: formData.paymentMethod,
                        label:
                          paymentMethodValues.find(
                            (paymentMethod) =>
                              paymentMethod.value === formData.paymentMethod,
                          )?.label || formData.paymentMethod,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  setFormData('paymentMethod', selectedOption.value);
                }}
                options={paymentMethodValues.map((paymentMethod) => ({
                  value: paymentMethod.value,
                  label: paymentMethod.label,
                }))}
                isDisabled={formData.isTrusted}
                error={formErrors.paymentMethod}
                helperText={
                  formErrors.paymentMethod
                    ? t(
                        'AGENCY_MANAGER.AGENCY_DETAILS.FIELDS.paymentMethodRequired',
                      )
                    : ''
                }
                menuPortalTarget={document.body}
              />
            </FormField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const FormField = ({ label, isEditMode, children, readOnlyValue }) => {
  const { caption } = typography;

  return (
    <>
      <SoftTypography color="secondary" fontSize={caption.fontSize}>
        {label}
      </SoftTypography>
      {isEditMode ? (
        children
      ) : (
        <SoftTypography variant="body2">{readOnlyValue}</SoftTypography>
      )}
    </>
  );
};

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  children: PropTypes.node,
  readOnlyValue: PropTypes.node,
};

AgencyDetailsForm.propTypes = {
  formData: PropTypes.shape({
    identifier: PropTypes.string,
    isAgreementSigned: PropTypes.bool,
    isActive: PropTypes.bool,
    email: PropTypes.string,
    mailingAddress: PropTypes.string,
    phoneNumber: PropTypes.string,
    abn: PropTypes.string,
    shopName: PropTypes.string,
    brands: PropTypes.array,
    partner: PropTypes.string,
    paymentMethod: PropTypes.string,
    isTrusted: PropTypes.bool,
    merchantOfRecord: PropTypes.string,
    country: PropTypes.string,
    bankAccountInfo: PropTypes.string,
    agreementSignedDate: PropTypes.string,
  }).isRequired,
  formErrors: PropTypes.object.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  isNewAgency: PropTypes.bool,
  setFormData: PropTypes.func.isRequired,
};

export default AgencyDetailsForm;
